import { useApiReferenceServices } from "@/lib/api-reference";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Brain, ChevronLeft, Menu, MoreVertical, Plus } from "lucide-react";
import { type JSX, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NavLink, Navigate, Outlet, useLocation, useNavigate, useParams } from "react-router";

import { FeedbackDialogTrigger } from "@/components/feedback-dialog";
import { AccountNavPanel } from "@/components/sidebar/account-nav-panel";
import { SettingsNavPanel } from "@/components/sidebar/settings-nav-panel";
import { useToast } from "@/components/ui/toast-provider";

import {
  ACTIVE_SESSION_QUERY_KEY,
  MESSAGES_STALE_MS,
  archiveSession,
  getActiveSession,
  getGeneratingBatch,
  getMessages,
  listSessions,
  messagesQueryKey,
  patchSessionTitle,
  unarchiveSession,
} from "@/lib/chat";
import { copyTextToClipboard } from "@/lib/clipboard";
import { type DocsShell, DocsShellContext } from "@/lib/docs-shell";
import { formatDate, formatTimestamp } from "@/lib/format";
import {
  type PrimaryNavItem,
  activePrimarySection,
  isPrimaryNavActive,
  primaryNavForRole,
  primaryNavRowClass,
  primaryNavRows,
} from "@/lib/navigation";
import { supabase, useSession } from "@/lib/supabase";
import { isDark } from "@/lib/theme";
import { useTheme } from "@/lib/theme-context";
import { USER_MEMORY_UNREAD_QUERY_KEY, userMemoryApi } from "@/lib/user-memory";
import { cn } from "@/lib/utils";

const SIDE_TABS = ["activos", "train", "archivados"] as const;
type SideTab = (typeof SIDE_TABS)[number];

/** Short labels so the 260px sidebar fits the active chip, ⋮, Slack and MCP. */
const SIDE_TAB_LABEL: Record<SideTab, string> = {
  activos: "Activos",
  train: "Train",
  archivados: "Archivados",
};

const SIDE_TAB_TITLE: Record<SideTab, string> = {
  activos: "Sesiones activas",
  train: "Sesiones con entrenamiento",
  archivados: "Sesiones archivadas",
};

/** Tailwind `lg` — keep in sync with `lg:*` utilities in this shell. */
const LG_MIN_WIDTH_PX = 1024;

function isLgViewport(): boolean {
  return window.innerWidth >= LG_MIN_WIDTH_PX;
}

function sessionOriginVisible(
  origin: "web" | "slack" | "mcp",
  showMcpSessions: boolean,
  showSlackSessions: boolean,
): boolean {
  if (origin === "mcp") return showMcpSessions;
  if (origin === "slack") return showSlackSessions;
  return true;
}

/** Origin chip beside the status filter. Pressed means those sessions stay in the list. */
function OriginVisibilityToggle({
  label,
  pressed,
  onClick,
}: {
  label: "MCP" | "Slack";
  pressed: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      aria-label={`mostrar chats ${label}`}
      aria-pressed={pressed}
      title={pressed ? `Ocultar chats ${label}` : `Mostrar chats ${label}`}
      onClick={onClick}
      className={cn(
        "shrink-0 rounded-full px-2 py-1 text-[10px] font-semibold tracking-wide transition-colors",
        pressed
          ? "border border-brand-extra-2-40 bg-brand-extra-2-40/20 text-foreground"
          : "border border-transparent bg-muted/80 text-muted-foreground/55 hover:bg-muted hover:text-muted-foreground",
      )}
    >
      {label}
    </button>
  );
}

/** Keep in sync with `GENERATING_POLL_MS` in `routes/chat.tsx`. */
const SESSION_LIVE_POLL_MS = 4000;

/** Fixed left column in session rows — reserved for status/icons. */
const SESSION_ICON_SLOT_CLASS = "flex w-5 shrink-0 items-center justify-start pl-0";

function SessionFactIcon({
  hasApproved,
  hasPending,
}: {
  hasApproved: boolean;
  hasPending: boolean;
}): JSX.Element | null {
  if (hasApproved) {
    return (
      <Brain
        data-fact="approved"
        aria-label="entrenamiento aprobado"
        className="size-3.5 shrink-0 text-muted-foreground/50"
        strokeWidth={1.75}
      />
    );
  }
  if (hasPending) {
    return (
      <Brain
        data-fact="pending"
        aria-label="entrenamiento pendiente"
        className="size-3.5 shrink-0 text-muted-foreground/35"
        strokeWidth={1.75}
      />
    );
  }
  return null;
}

/** Live/in-flight marker — theme accent (`--brand-accent` via extra-2-40). */
function SessionLiveDot(): JSX.Element {
  return (
    <span
      data-session-live-dot=""
      aria-hidden="true"
      className="ml-1.5 inline-block size-1.5 shrink-0 animate-pulse rounded-full bg-brand-extra-2-40 motion-reduce:animate-none"
    />
  );
}

/** Fades the title before the hover menu so glyphs don't run under ⋮. */
const SESSION_TITLE_HOVER_MASK =
  "[mask-image:linear-gradient(to_right,black_0%,black_62%,transparent_91%)] [-webkit-mask-image:linear-gradient(to_right,black_0%,black_62%,transparent_91%)]";
const SESSION_TITLE_GROUP_HOVER_MASK =
  "group-hover:[mask-image:linear-gradient(to_right,black_0%,black_62%,transparent_91%)] group-hover:[-webkit-mask-image:linear-gradient(to_right,black_0%,black_62%,transparent_91%)]";

function PrimaryNavLink({
  tab,
  pathname,
  hasUnreadMemory,
}: {
  tab: PrimaryNavItem;
  pathname: string;
  hasUnreadMemory: boolean;
}): JSX.Element {
  return (
    <NavLink
      to={tab.to}
      end={tab.end ?? false}
      className={({ isActive }) => {
        const active = isActive || isPrimaryNavActive(pathname, tab);
        return cn(
          // Cells stay on one line: work labels are short; settings and
          // "mi cuenta" share the wider second row.
          "relative flex items-center justify-center gap-1 whitespace-nowrap rounded-md px-2 py-1.5 text-body-3 transition-colors",
          active
            ? "bg-muted font-semibold text-foreground"
            : "text-muted-foreground hover:bg-muted/50 hover:text-foreground",
        );
      }}
    >
      <tab.icon aria-hidden="true" className="size-3.5 shrink-0" strokeWidth={1.75} />
      {tab.label}
      {tab.to === "/account" && hasUnreadMemory && (
        <span
          aria-label="memoria actualizada"
          className="absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full bg-brand-extra-2-40"
        />
      )}
    </NavLink>
  );
}

/**
 * Shared chat-history state surfaced to the chat route via Outlet context.
 * `activeSessionId` is derived from the URL (`/chat/:sessionId?`) — the URL
 * is the single source of truth for the open session, never component state.
 * - `activeSessionId === null` means "no session yet — next POST /chat
 *   allocates a new chat_sessions row server-side."
 * - `setActiveSessionId(null)` triggers `+ nuevo chat`: navigates to bare
 *   `/chat` (history entry) and the chat route clears its in-memory turns.
 * - `setActiveSessionId(id)` navigates to `/chat/<id>` as a history entry —
 *   use for user-driven selection (sidebar pick, resume banner).
 * - `replaceActiveSessionId(id)` corrects the URL in place (no history
 *   entry) — use when the chat route learns the real session id after the
 *   fact (new session's first SSE turn, voice-training reconnect, entering
 *   refine-knowledge mode).
 */
export interface ChatShellContext {
  activeSessionId: string | null;
  setActiveSessionId: (id: string | null) => void;
  replaceActiveSessionId: (id: string | null) => void;
  hasSessions: boolean;
  sessionsLoading: boolean;
}

export function AppShell(): JSX.Element {
  const { showToast } = useToast();
  const [sideTab, setSideTab] = useState<SideTab>("activos");
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const [showMcpSessions, setShowMcpSessions] = useState(true);
  const [showSlackSessions, setShowSlackSessions] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(() => isLgViewport());
  // The element the docs or graph route portals its tools into (only rendered
  // while one of those modules is open).
  const [docsNavHost, setDocsNavHost] = useState<HTMLElement | null>(null);
  // The slot before the panel where the docs route renders its skip links.
  const [docsSkipHost, setDocsSkipHost] = useState<HTMLElement | null>(null);
  // Focus handling of the docs drawer: each `openNavigation` bumps the tick, an
  // effect then moves focus into the panel; `returnFocusRef` is what had focus
  // when it opened, so closing can give it back.
  const [navFocusTick, setNavFocusTick] = useState(0);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const asideRef = useRef<HTMLElement>(null);
  const openMenuRef = useRef<HTMLButtonElement>(null);
  const wasOpenRef = useRef(sidebarOpen);
  const handledFocusTickRef = useRef(0);
  // The URL is the source of truth for the open session — no local state.
  const params = useParams<{ sessionId?: string }>();
  const activeSessionId = params.sessionId ?? null;
  // Set by the explicit "+ nuevo chat" action so the boot-redirect effect
  // below does not immediately bounce the blank view back to the most
  // recent session. Lives for the component's lifetime (not "once"): the
  // user's "I want a blank chat" intent should stick until they either pick
  // a real session or a new session materialises its own id.
  const suppressAutoRedirectRef = useRef(false);
  const [visibleCount, setVisibleCount] = useState(15);
  const [openMenu, setOpenMenu] = useState<{ id: string; top: number; left: number } | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState("");
  const renameInputRef = useRef<HTMLInputElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const section = activePrimarySection(location.pathname);
  const closeSidebarIfMobile = (): void => {
    if (!isLgViewport()) setSidebarOpen(false);
  };
  const docsShell = useMemo<DocsShell>(
    () => ({
      host: docsNavHost,
      skipHost: docsSkipHost,
      openNavigation: () => {
        const active = document.activeElement;
        returnFocusRef.current = active instanceof HTMLElement ? active : null;
        setSidebarOpen(true);
        setNavFocusTick((tick) => tick + 1);
      },
      closeNavigation: () => {
        if (isLgViewport()) return false;
        setSidebarOpen(false);
        return true;
      },
    }),
    [docsNavHost, docsSkipHost],
  );

  const docsActive = section === "api-reference";
  const graphActive = section === "graph";
  // Both modules host their own content in the panel; the drawer behaves the same.
  const hostedPanel = docsActive || graphActive;
  // Opening the docs drawer (or the skip link to the navigation) puts focus in
  // the panel; the drawer's `inert` is already gone when this effect runs.
  useEffect(() => {
    if (navFocusTick === handledFocusTickRef.current || !docsNavHost) return;
    handledFocusTickRef.current = navFocusTick;
    docsNavHost.focus();
  }, [navFocusTick, docsNavHost]);
  // Escape closes the docs drawer below lg wherever focus is inside it. The docs
  // navigation is a React portal into the panel: its synthetic key events bubble
  // through the React tree (the docs layout), never through the panel's own
  // handlers, so the listener is native, on the panel node, and lives only while
  // the drawer is open.
  useEffect(() => {
    const panel = asideRef.current;
    if (!panel || !sidebarOpen || !hostedPanel) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== "Escape" || event.defaultPrevented || isLgViewport()) return;
      event.stopPropagation();
      setSidebarOpen(false);
    };
    panel.addEventListener("keydown", onKeyDown);
    return () => panel.removeEventListener("keydown", onKeyDown);
  }, [sidebarOpen, hostedPanel]);
  // Closing the docs panel must not strand focus on a node that just became
  // inert: when focus was inside it (or lost to the body), it goes back to what
  // opened it, or to "Abrir menú". A link that already moved focus to the
  // content is left alone.
  useEffect(() => {
    const wasOpen = wasOpenRef.current;
    wasOpenRef.current = sidebarOpen;
    if (!wasOpen || sidebarOpen || !hostedPanel) return;
    const active = document.activeElement;
    const stranded =
      !active || active === document.body || asideRef.current?.contains(active) === true;
    const back = returnFocusRef.current;
    returnFocusRef.current = null;
    if (!stranded) return;
    (back?.isConnected ? back : openMenuRef.current)?.focus();
  }, [sidebarOpen, hostedPanel]);

  // Push navigation for user-driven session selection: a history entry per
  // pick, so browser back/forward moves between previously opened sessions.
  // No search string is carried over — switching sessions (or starting a
  // blank one) intentionally drops any `?m=`/`?refine-knowledge=` left from
  // whatever was on screen before.
  const setActiveSessionId = useCallback(
    (id: string | null): void => {
      navigate(id === null ? "/chat" : `/chat/${id}`);
    },
    [navigate],
  );
  // Replace navigation for corrections: the chat route learning the real
  // session id after the fact. Preserves the current query string (e.g. the
  // `?refine-knowledge=` param that triggered the correction) — only the
  // path segment changes.
  const replaceActiveSessionId = useCallback(
    (id: string | null): void => {
      const pathname = id === null ? "/chat" : `/chat/${id}`;
      if (pathname === location.pathname) return;
      navigate({ pathname, search: location.search }, { replace: true });
    },
    // oxlint-disable-next-line react-doctor/no-mutable-in-deps -- `location` is the reactive object from useLocation(), not `window.location`
    [navigate, location.pathname, location.search],
  );

  // Clicking a chat from the sidebar must take the user to the chat route.
  // Without this jump the highlight changes but the user is still on
  // /settings/... and never sees the conversation. "+ nuevo chat" suppresses
  // the boot-redirect below so the blank view sticks; picking a real session
  // clears that suppression.
  const onPickSession = (id: string | null): void => {
    suppressAutoRedirectRef.current = id === null;
    setActiveSessionId(id);
  };

  // Graph has no sidebar submenus — close on mobile when entering that
  // section. Settings / Account keep the drawer open so the user can pick
  // a submenu item; those panels call `closeSidebarIfMobile` themselves.
  // The docs module is the same below lg: its header offers "Navegación", which
  // opens this drawer on demand, so entering the module leaves it closed.
  useEffect(() => {
    if (
      (location.pathname.startsWith("/graph") || location.pathname.startsWith("/api-reference")) &&
      !isLgViewport()
    ) {
      setSidebarOpen(false);
    }
    // oxlint-disable-next-line react-doctor/no-mutable-in-deps -- `location` is the reactive object from useLocation(), not `window.location`
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname !== "/") return;
    const handler = () => setSidebarOpen(isLgViewport());
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
    // oxlint-disable-next-line react-doctor/no-mutable-in-deps -- `location` is the reactive object from useLocation(), not `window.location`
  }, [location.pathname]);

  useEffect(() => {
    if (!filterMenuOpen) return;
    const closeOnOutside = (e: MouseEvent) => {
      if ((e.target as HTMLElement).closest("[data-session-filter-menu]")) return;
      setFilterMenuOpen(false);
    };
    const closeOnKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setFilterMenuOpen(false);
    };
    document.addEventListener("mousedown", closeOnOutside);
    document.addEventListener("keydown", closeOnKey);
    return () => {
      document.removeEventListener("mousedown", closeOnOutside);
      document.removeEventListener("keydown", closeOnKey);
    };
  }, [filterMenuOpen]);

  useEffect(() => {
    if (!openMenu) return;
    const closeOnOutside = (e: MouseEvent) => {
      if ((e.target as HTMLElement).closest("[data-session-menu]")) return;
      setOpenMenu(null);
    };
    const closeOnScroll = () => setOpenMenu(null);
    document.addEventListener("mousedown", closeOnOutside);
    window.addEventListener("scroll", closeOnScroll, true);
    return () => {
      document.removeEventListener("mousedown", closeOnOutside);
      window.removeEventListener("scroll", closeOnScroll, true);
    };
  }, [openMenu]);
  const session = useSession();
  const { theme } = useTheme();
  const dark = isDark(theme);
  const role = session.status === "authenticated" ? session.role : undefined;
  const apiReference = useApiReferenceServices(session.status === "authenticated");
  const primaryNav = role
    ? primaryNavForRole(role).filter(
        (item) => !item.requiresApiReferenceFlag || apiReference.isSuccess,
      )
    : [];
  const unreadQuery = useQuery({
    queryKey: USER_MEMORY_UNREAD_QUERY_KEY,
    queryFn: () => userMemoryApi.unread(),
    enabled: session.status === "authenticated",
  });
  const hasUnreadMemory =
    (unreadQuery.data?.unread ?? false) || (unreadQuery.data?.pending_suggestions ?? 0) > 0;

  const queryClient = useQueryClient();
  const chatSectionActive = section === "chat";
  const sessionsQuery = useQuery({
    queryKey: ["chat", "sessions", "active", visibleCount],
    queryFn: () => listSessions({ limit: visibleCount, archived: false }),
    enabled: session.status === "authenticated" && chatSectionActive,
    staleTime: 5_000,
    placeholderData: keepPreviousData,
  });
  const archivedSessionsQuery = useQuery({
    queryKey: ["chat", "sessions", "archived", visibleCount],
    queryFn: () => listSessions({ limit: visibleCount, archived: true }),
    enabled: session.status === "authenticated" && chatSectionActive && sideTab === "archivados",
    staleTime: 5_000,
    placeholderData: keepPreviousData,
  });
  const hasMore = sessionsQuery.data !== undefined && sessionsQuery.data.length >= visibleCount;

  const invalidateAllSessions = (): void => {
    void queryClient.invalidateQueries({ queryKey: ["chat", "sessions"] });
  };
  // react-doctor's `query-mutation-missing-invalidation` does not follow
  // indirection — both mutations below invalidate via `invalidateAllSessions`.
  /* oxlint-disable react-doctor/query-mutation-missing-invalidation */
  const archiveMutation = useMutation({
    mutationFn: (id: string) => archiveSession(id),
    onSuccess: invalidateAllSessions,
  });
  const unarchiveMutation = useMutation({
    mutationFn: (id: string) => unarchiveSession(id),
    onSuccess: invalidateAllSessions,
  });
  const renameMutation = useMutation({
    mutationFn: ({ id, title }: { id: string; title: string }) => patchSessionTitle(id, title),
    onSuccess: () => {
      setRenamingId(null);
      setRenameDraft("");
      invalidateAllSessions();
    },
  });
  /* oxlint-enable react-doctor/query-mutation-missing-invalidation */

  // Focus and select the inline rename input the moment it appears so the
  // user can start typing immediately without an extra click.
  useEffect(() => {
    if (renamingId !== null) {
      renameInputRef.current?.focus();
      renameInputRef.current?.select();
    }
  }, [renamingId]);

  // `activos` is the full active list, `train` filters those that produced
  // a chat-learned fact (pending or approved), `archivados` reads from the
  // separately-keyed archived query.
  const filteredSessions = useMemo(() => {
    const applyOriginFilter = (sessions: typeof sessionsQuery.data) =>
      (sessions ?? []).filter((item) =>
        sessionOriginVisible(item.origin, showMcpSessions, showSlackSessions),
      );
    if (sideTab === "archivados") return applyOriginFilter(archivedSessionsQuery.data);
    const all = applyOriginFilter(sessionsQuery.data);
    if (sideTab === "train") {
      return all.filter((s) => s.has_pending_chat_fact || s.has_approved_chat_fact);
    }
    return all;
  }, [sessionsQuery.data, archivedSessionsQuery.data, sideTab, showMcpSessions, showSlackSessions]);

  const pollLiveSessions =
    session.status === "authenticated" && chatSectionActive && sideTab !== "archivados";

  const activeSessionQuery = useQuery({
    queryKey: ACTIVE_SESSION_QUERY_KEY,
    queryFn: getActiveSession,
    enabled: pollLiveSessions,
    staleTime: 10_000,
    refetchInterval: SESSION_LIVE_POLL_MS,
    refetchOnWindowFocus: true,
  });

  const visibleSessionIds = useMemo(() => filteredSessions.map((s) => s.id), [filteredSessions]);
  // One batched poll for the whole visible window instead of one request per
  // listed session — see `optimize-chat-sidebar-generating-poll`.
  const generatingBatchQuery = useQuery({
    queryKey: ["chat", "sessions", "generating-batch", visibleSessionIds],
    queryFn: (): Promise<Record<string, boolean>> =>
      getGeneratingBatch(visibleSessionIds).catch(() => ({})),
    refetchInterval: SESSION_LIVE_POLL_MS,
    enabled: pollLiveSessions && visibleSessionIds.length > 0,
    staleTime: 2_000,
  });

  const liveSessionIds = useMemo(() => {
    const ids = new Set<string>();
    const inFlight = activeSessionQuery.data;
    if (inFlight?.reason === "in_flight") {
      ids.add(inFlight.session_id);
    }
    const batch = generatingBatchQuery.data ?? {};
    for (const s of filteredSessions) {
      if (batch[s.id] === true) {
        ids.add(s.id);
      }
    }
    return ids;
  }, [activeSessionQuery.data, filteredSessions, generatingBatchQuery.data]);

  // Bare `/chat` (or `/`) with existing sessions replace-redirects to the
  // most recent one once the session list loads. Scoped to those two exact
  // paths (not just "no session param") so landing on /settings or /graph
  // while activeSessionId happens to be null never redirects away from
  // them. `+ nuevo chat` sets the suppression ref so the blank view sticks.
  useEffect(() => {
    if (location.pathname !== "/" && location.pathname !== "/chat") return;
    if (suppressAutoRedirectRef.current) return;
    const first = sessionsQuery.data?.[0];
    if (!first) return;
    navigate(`/chat/${first.id}`, { replace: true });
    // oxlint-disable-next-line react-doctor/no-mutable-in-deps -- `location` is the reactive object from useLocation(), not `window.location`
  }, [location.pathname, sessionsQuery.data, navigate]);

  if (session.status === "loading") {
    return (
      <main className="grid h-screen place-items-center text-body-2 text-muted-foreground">
        Cargando sesión…
      </main>
    );
  }
  if (session.status === "anonymous") {
    return (
      <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />
    );
  }

  return (
    <div
      className={cn(
        "grid h-screen bg-background",
        sidebarOpen &&
          (docsActive
            ? "lg:grid-cols-[var(--docs-nav-width,18rem)_1fr]"
            : graphActive
              ? "lg:grid-cols-[var(--graph-nav-width,18rem)_1fr]"
              : "lg:grid-cols-[260px_1fr]"),
      )}
    >
      {/* api_reference spec, "The internal docs module can be operated by keyboard
          without crossing the navigation": the docs route renders its skip links
          here, before the drawer's backdrop and the panel, so they are the page's
          first focusable elements. `contents`: the slot is no grid cell. */}
      {docsActive && (
        <div
          ref={setDocsSkipHost}
          data-testid="docs-skip-slot"
          data-docs-scope
          className="contents"
        />
      )}
      {/* backdrop for mobile sidebar */}
      {sidebarOpen && (
        <button
          type="button"
          aria-label="Cerrar menú de navegación"
          className="fixed inset-0 z-40 bg-black/40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* A closed panel is only translated off-screen, so it is also `inert` and
          hidden from assistive technology: its links are not tab stops. Escape
          closes the drawer below lg (at lg and above the panel is persistent). */}
      <aside
        ref={asideRef}
        inert={!sidebarOpen}
        aria-hidden={sidebarOpen ? undefined : true}
        className={cn(
          "fixed inset-y-0 left-0 z-50 flex h-screen w-[260px] flex-col border-r border-border bg-background transition-transform",
          docsActive && "lg:w-[var(--docs-nav-width,18rem)]",
          graphActive && "lg:w-[var(--graph-nav-width,18rem)]",
          sidebarOpen ? "translate-x-0 lg:relative" : "-translate-x-full",
        )}
      >
        <div className="border-b border-border px-4 py-3">
          <div className="flex items-center justify-between gap-2">
            <NavLink
              to="/"
              aria-label="Ir al inicio"
              className="inline-flex min-w-0 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              onClick={() => {
                if (!isLgViewport()) setSidebarOpen(false);
              }}
            >
              <img
                src={dark ? "/beflow-logo-white.svg" : "/beflow-logo.svg"}
                alt="BeFlow"
                className="h-8"
              />
            </NavLink>
            {sidebarOpen && (
              <button
                type="button"
                onClick={() => setSidebarOpen(false)}
                aria-label="Cerrar menú"
                className="shrink-0 rounded-md border border-border bg-background/80 p-1.5 text-muted-foreground shadow-sm hover:text-foreground"
              >
                <ChevronLeft size={14} />
              </button>
            )}
          </div>
          <nav aria-label="Navegacion principal" className="mt-3 flex flex-col gap-1">
            {primaryNavRows(primaryNav).map((row) => (
              <div
                key={row.map((item) => item.to).join("|")}
                className={cn("grid gap-1", primaryNavRowClass(row.length))}
              >
                {row.map((tab) => (
                  <PrimaryNavLink
                    key={tab.to}
                    tab={tab}
                    pathname={location.pathname}
                    hasUnreadMemory={hasUnreadMemory}
                  />
                ))}
              </div>
            ))}
          </nav>
        </div>

        {section === "chat" ? (
          <>
            <div className="border-b border-border p-3">
              <button
                type="button"
                onClick={() => {
                  onPickSession(null);
                  closeSidebarIfMobile();
                }}
                className="flex w-full items-center justify-center gap-1 rounded-md border border-dashed border-border py-2 text-body-3 text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <Plus size={14} /> nuevo chat
              </button>
            </div>

            <div className="relative z-20 flex items-center gap-1.5 border-b border-border px-2.5 py-2">
              <div
                className="relative inline-flex min-w-0 items-center gap-0.5 rounded-lg bg-muted/70 p-0.5"
                data-session-filter-menu
              >
                <div role="tablist" aria-label="Filtro de sesiones">
                  <button
                    type="button"
                    role="tab"
                    aria-selected="true"
                    aria-haspopup="menu"
                    aria-expanded={filterMenuOpen}
                    title={SIDE_TAB_TITLE[sideTab]}
                    onClick={() => setFilterMenuOpen((open) => !open)}
                    className="block rounded-md bg-background px-2.5 py-1.5 text-[10px] font-semibold leading-tight tracking-tight text-foreground shadow-sm"
                  >
                    {SIDE_TAB_LABEL[sideTab]}
                  </button>
                </div>
                <button
                  type="button"
                  aria-label="Cambiar filtro de sesiones"
                  aria-haspopup="menu"
                  aria-expanded={filterMenuOpen}
                  title="Cambiar filtro de sesiones"
                  onClick={() => setFilterMenuOpen((open) => !open)}
                  className="rounded-md p-1 text-muted-foreground hover:bg-background hover:text-foreground"
                >
                  <MoreVertical size={14} />
                </button>
                {filterMenuOpen && (
                  <div
                    role="menu"
                    aria-label="Filtro de sesiones"
                    className="absolute left-0 top-full z-50 mt-1 min-w-[148px] rounded-md border border-border bg-card py-1 text-card-foreground shadow-xl"
                  >
                    {SIDE_TABS.filter((tab) => tab !== sideTab).map((tab) => (
                      <button
                        key={tab}
                        type="button"
                        role="menuitem"
                        title={SIDE_TAB_TITLE[tab]}
                        onClick={() => {
                          setSideTab(tab);
                          setFilterMenuOpen(false);
                        }}
                        className="w-full px-3 py-1.5 text-left text-body-3 hover:bg-accent"
                      >
                        {SIDE_TAB_LABEL[tab]}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="ml-auto flex shrink-0 items-center gap-1">
                <OriginVisibilityToggle
                  label="Slack"
                  pressed={showSlackSessions}
                  onClick={() => setShowSlackSessions((visible) => !visible)}
                />
                <OriginVisibilityToggle
                  label="MCP"
                  pressed={showMcpSessions}
                  onClick={() => setShowMcpSessions((visible) => !visible)}
                />
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-2 py-3">
              {(sideTab === "archivados" ? archivedSessionsQuery : sessionsQuery).isLoading ? (
                <p className="px-2 text-body-3 text-muted-foreground">Cargando…</p>
              ) : filteredSessions.length > 0 ? (
                <>
                  <ul className="space-y-2">
                    {filteredSessions.map((s) => {
                      const archived = s.archived_at !== null && s.archived_at !== undefined;
                      const showFactIcon = s.has_approved_chat_fact || s.has_pending_chat_fact;
                      const isLive = liveSessionIds.has(s.id);
                      const pendingMutation =
                        (archived &&
                          unarchiveMutation.isPending &&
                          unarchiveMutation.variables === s.id) ||
                        (!archived &&
                          archiveMutation.isPending &&
                          archiveMutation.variables === s.id);
                      return (
                        <li key={s.id} data-session-live={isLive ? "true" : undefined}>
                          <div
                            data-session-live={isLive ? "true" : undefined}
                            className={cn(
                              "group relative flex items-center rounded-md transition",
                              activeSessionId === s.id
                                ? "bg-muted text-foreground"
                                : "text-muted-foreground hover:bg-muted/50 hover:text-foreground",
                            )}
                          >
                            <div
                              className={SESSION_ICON_SLOT_CLASS}
                              aria-hidden={!(isLive || showFactIcon)}
                            >
                              {isLive ? (
                                <SessionLiveDot />
                              ) : (
                                <SessionFactIcon
                                  hasApproved={s.has_approved_chat_fact}
                                  hasPending={s.has_pending_chat_fact}
                                />
                              )}
                            </div>
                            {renamingId === s.id ? (
                              <div className="min-w-0 flex-1 py-1.5 pr-2">
                                <input
                                  ref={renameInputRef}
                                  value={renameDraft}
                                  onChange={(e) => setRenameDraft(e.target.value)}
                                  onClick={(e) => e.stopPropagation()}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" && renameDraft.trim().length > 0) {
                                      renameMutation.mutate({
                                        id: s.id,
                                        title: renameDraft.trim(),
                                      });
                                    } else if (e.key === "Escape") {
                                      setRenamingId(null);
                                      setRenameDraft("");
                                    }
                                  }}
                                  onBlur={() => {
                                    setRenamingId(null);
                                    setRenameDraft("");
                                  }}
                                  placeholder="nuevo título"
                                  aria-label="Nuevo título de la sesión"
                                  maxLength={60}
                                  className="w-full rounded border border-border bg-background px-2 py-0.5 text-body-3 text-foreground outline-none focus:border-brand-extra-2-40"
                                />
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={() => onPickSession(s.id)}
                                onMouseEnter={() => {
                                  // Warm the message cache on hover so the click
                                  // renders instantly (cache-first switch in the
                                  // chat route). `prefetchQuery` is a no-op when the
                                  // cache is already fresh, so hovering won't spam.
                                  void queryClient.prefetchQuery({
                                    queryKey: messagesQueryKey(s.id),
                                    queryFn: () => getMessages(s.id),
                                    staleTime: MESSAGES_STALE_MS,
                                  });
                                }}
                                title={`${s.title ?? "sin título"} · ${formatRelativeTime(s.last_message_at)}${isLive ? " · en curso" : ""}`}
                                className="relative z-0 min-w-0 flex-1 py-1.5 pl-0 pr-10 text-left"
                              >
                                <span className="flex min-w-0 items-center gap-1.5">
                                  <span
                                    className={cn(
                                      "block min-w-0 truncate text-body-3 transition-[mask-image,-webkit-mask-image] duration-150",
                                      "group-hover:text-clip",
                                      SESSION_TITLE_GROUP_HOVER_MASK,
                                      openMenu?.id === s.id && SESSION_TITLE_HOVER_MASK,
                                    )}
                                  >
                                    {s.title ?? "sin título"}
                                  </span>
                                  {s.origin === "mcp" && (
                                    <span
                                      data-testid={`mcp-badge-${s.id}`}
                                      className="shrink-0 rounded border border-brand-extra-2-40/50 px-1 py-px text-[9px] font-semibold leading-none text-brand-extra-2-40"
                                    >
                                      MCP
                                    </span>
                                  )}
                                  {s.origin === "slack" && (
                                    <span
                                      data-testid={`slack-badge-${s.id}`}
                                      className="shrink-0 rounded border border-brand-extra-2-40/50 px-1 py-px text-[9px] font-semibold leading-none text-brand-extra-2-40"
                                    >
                                      Slack
                                    </span>
                                  )}
                                </span>
                              </button>
                            )}
                            <SessionLastUsedLabel
                              iso={s.last_message_at}
                              menuOpen={openMenu?.id === s.id}
                            />
                            <div
                              className={cn(
                                "absolute inset-y-0 right-0 z-10 flex items-center pr-1 transition-opacity",
                                openMenu?.id === s.id
                                  ? "pointer-events-auto opacity-100"
                                  : "pointer-events-none opacity-0 group-hover:pointer-events-auto group-hover:opacity-100",
                              )}
                              data-session-menu
                            >
                              <button
                                type="button"
                                aria-label="Opciones"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (openMenu?.id === s.id) {
                                    setOpenMenu(null);
                                    return;
                                  }
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  setOpenMenu({
                                    id: s.id,
                                    top: rect.top,
                                    left: rect.right + 4,
                                  });
                                }}
                                className="rounded p-0.5 hover:bg-muted hover:text-foreground focus-visible:opacity-100"
                              >
                                <MoreVertical size={14} />
                              </button>
                              {openMenu?.id === s.id && (
                                <div
                                  style={{ top: openMenu.top, left: openMenu.left }}
                                  className="fixed z-50 min-w-[140px] rounded-md border border-border bg-card py-1 text-card-foreground shadow-xl"
                                >
                                  <button
                                    type="button"
                                    data-testid={`copy-id-${s.id}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      void copyTextToClipboard(s.id).then((ok) => {
                                        showToast({
                                          kind: ok ? "success" : "error",
                                          message: ok
                                            ? "ID de sesión copiado."
                                            : "No se pudo copiar el ID de sesión.",
                                        });
                                      });
                                    }}
                                    className="flex w-full items-center justify-between gap-3 px-3 py-1.5 text-left text-body-3 hover:bg-accent"
                                  >
                                    <span>Copiar ID de sesión</span>
                                  </button>
                                  <button
                                    type="button"
                                    data-testid={`rename-${s.id}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setRenamingId(s.id);
                                      setRenameDraft(s.title ?? "");
                                      setOpenMenu(null);
                                    }}
                                    className="w-full px-3 py-1.5 text-left text-body-3 hover:bg-accent"
                                  >
                                    renombrar
                                  </button>
                                  <button
                                    type="button"
                                    disabled={pendingMutation}
                                    data-testid={`${archived ? "unarchive" : "archive"}-${s.id}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (archived) unarchiveMutation.mutate(s.id);
                                      else archiveMutation.mutate(s.id);
                                      setOpenMenu(null);
                                    }}
                                    className="w-full px-3 py-1.5 text-left text-body-3 hover:bg-accent disabled:opacity-50"
                                  >
                                    {archived ? "Desarchivar" : "Archivar"}
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                  {hasMore && (
                    <button
                      type="button"
                      onClick={() => setVisibleCount((n) => n + 10)}
                      disabled={sessionsQuery.isFetching}
                      className="mt-2 w-full rounded-md px-2 py-1.5 text-body-3 text-muted-foreground hover:bg-muted/50 hover:text-foreground disabled:opacity-50"
                    >
                      {sessionsQuery.isFetching ? "cargando…" : "ver más"}
                    </button>
                  )}
                </>
              ) : (
                <p className="px-2 text-body-3 text-muted-foreground">
                  {sideTab === "train"
                    ? "Aún no hay entrenamiento."
                    : sideTab === "archivados"
                      ? "Aún no hay conversaciones archivadas."
                      : "Aún no hay conversaciones."}
                </p>
              )}
            </div>
          </>
        ) : (
          <div
            className={cn(
              "min-h-0 flex-1 overflow-y-auto",
              // The docs header is sticky: scrolled-to focus must clear it.
              docsActive && "scroll-pt-44",
            )}
          >
            {section === "settings" ? (
              <SettingsNavPanel
                role={session.role}
                onNavigate={closeSidebarIfMobile}
                sidebarOpen={sidebarOpen}
              />
            ) : section === "account" ? (
              <AccountNavPanel
                hasUnreadMemory={hasUnreadMemory}
                onNavigate={closeSidebarIfMobile}
              />
            ) : section === "api-reference" ? (
              // The docs route portals its navigation into this host (see
              // `lib/docs-shell.tsx`); a callback ref into state commits before
              // paint, so there is no frame without the navigation.
              <div
                key="docs-nav-host"
                ref={setDocsNavHost}
                id="docs-nav-host"
                data-testid="docs-nav-host"
                data-docs-scope
                tabIndex={-1}
                className="flex min-h-0 flex-1 flex-col outline-none"
              />
            ) : (
              // The graph page portals its tools here (graph_qa spec). No
              // `data-docs-scope`: that scopes the docs typography and contrast
              // tokens, which must not leak into the graph. `key` makes React
              // mount a fresh element, so the host state never holds a stale one.
              <div
                key="graph-tools-host"
                ref={setDocsNavHost}
                id="graph-tools-host"
                data-testid="graph-tools-host"
                tabIndex={-1}
                className="flex min-h-0 flex-1 flex-col outline-none"
              />
            )}
          </div>
        )}

        <div className="border-t border-border px-4 py-3">
          <p className="truncate text-body-3 text-foreground">{session.session.user.email}</p>
          <div className="mt-1 flex items-center gap-3">
            <button
              type="button"
              onClick={() => supabase.auth.signOut()}
              className="text-body-3 text-muted-foreground hover:text-foreground"
            >
              salir
            </button>
            <FeedbackDialogTrigger triggerClassName="ml-auto" />
          </div>
        </div>
      </aside>

      <main className="flex min-w-0 flex-col overflow-hidden">
        {!sidebarOpen && (
          <button
            ref={openMenuRef}
            type="button"
            onClick={() => setSidebarOpen(true)}
            aria-label="Abrir menú"
            className="fixed left-4 top-3 z-40 rounded-md border border-border bg-background/80 p-1.5 text-muted-foreground shadow-sm backdrop-blur hover:text-foreground"
          >
            <Menu size={14} />
          </button>
        )}
        {/*
         * `relative` is load-bearing: it makes this scroll container the
         * containing block for absolutely positioned content inside the
         * routed page. `sr-only` is `position: absolute` with no offsets, so
         * without a positioned ancestor such a node resolves against the
         * initial containing block — it escapes this container's clip and its
         * static position (hundreds of px down a long page) becomes real
         * document scroll height, i.e. dead space below the shell.
         */}
        <DocsShellContext.Provider value={docsShell}>
          <div className="relative flex-1 overflow-y-auto">
            <Outlet
              context={
                {
                  activeSessionId,
                  setActiveSessionId,
                  replaceActiveSessionId,
                  hasSessions: (sessionsQuery.data?.length ?? 0) > 0,
                  sessionsLoading: sessionsQuery.isLoading,
                } satisfies ChatShellContext
              }
            />
          </div>
        </DocsShellContext.Provider>
      </main>
    </div>
  );
}

/** Tiny inline relative-time formatter — keeps the dependency surface flat. */
function formatRelativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffMin = Math.floor((now - then) / 60_000);
  if (diffMin < 1) return "ahora";
  if (diffMin < 60) return `hace ${diffMin} min`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `hace ${diffHr} h`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay === 1) return "ayer";
  if (diffDay < 7) return `hace ${diffDay} d`;
  return formatDate(iso);
}

/** Compact sidebar age label: 1m, 5m, 30m, 1hr, 1d, 100d, … */
export function formatSessionLastUsed(iso: string, nowMs: number = Date.now()): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";
  const diffMin = Math.max(1, Math.floor((nowMs - then) / 60_000));
  if (diffMin < 60) return `${diffMin}m`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}hr`;
  const diffDay = Math.floor(diffHr / 24);
  return `${diffDay}d`;
}

function SessionLastUsedLabel({
  iso,
  menuOpen,
}: {
  iso: string;
  menuOpen: boolean;
}): JSX.Element {
  return (
    <span
      className={cn(
        "pointer-events-none absolute inset-y-0 right-1.5 z-[1] flex items-center text-label-3 tabular-nums text-muted-foreground/55 transition-opacity",
        menuOpen ? "opacity-0" : "group-hover:opacity-0",
      )}
      title={formatTimestamp(iso)}
    >
      {formatSessionLastUsed(iso)}
    </span>
  );
}
